const ALLOWED_EXTENSIONS = ["mp4", "m4v", "mov"];
const EXPECTED_CONTENT_TYPES = ["video/mp4", "video/quicktime"];

function csrfToken() {
  if (typeof document === "undefined") return null;

  return document.querySelector('meta[name="csrf-token"]')?.content || null;
}

function extensionFor(filename) {
  const segments = String(filename || "").split(".");
  return segments.length > 1 ? segments.pop().toLowerCase() : "";
}

export function episodeVideoSourceUpload({ fetch: fetcher = globalThis.fetch } = {}) {
  return {
    source: null,
    completedParts: [],
    resumed: false,
    errorKey: null,
    warningKey: null,
    videoSourcesUrl: null,
    maxVideoBytes: 10 * 1024 * 1024 * 1024,

    init() {
      this.videoSourcesUrl = this.$root.dataset.videoSourcesUrl;
      this.maxVideoBytes = Number(this.$root.dataset.maxVideoBytes) || this.maxVideoBytes;
    },

    async selectFile(file) {
      this.errorKey = null;
      this.warningKey = null;

      if (!ALLOWED_EXTENSIONS.includes(extensionFor(file.name))) {
        this.errorKey = "unsupported_extension";
        return;
      }
      if (file.size > this.maxVideoBytes) {
        this.errorKey = "file_too_large";
        return;
      }
      if (file.type && !EXPECTED_CONTENT_TYPES.includes(file.type)) {
        this.warningKey = "unexpected_content_type";
      }

      let result;
      try {
        result = await this.requestPayload(this.videoSourcesUrl, {
          method: "POST",
          body: JSON.stringify({
            original_filename: file.name,
            byte_size: file.size,
            content_type_hint: file.type,
          }),
        });
      } catch (error) {
        this.errorKey = error.message;
        return;
      }
      const { response, payload } = result;

      this.resumed = response.status === 200;
      if (!this.resumed) this.completedParts = [];
      this.source = {
        id: payload.id,
        uploadId: payload.upload_id,
        key: payload.key,
      };
    },

    async signPart(partNumber) {
      const { payload } = await this.requestPayload(`${this.videoSourcesUrl}/${this.source.id}/sign_part`, {
        method: "POST",
        body: JSON.stringify({
          part_number: partNumber,
          upload_id: this.source.uploadId,
          key: this.source.key,
        }),
      });
      return payload;
    },

    async complete(parts) {
      const { payload } = await this.requestPayload(`${this.videoSourcesUrl}/${this.source.id}/complete`, {
        method: "POST",
        body: JSON.stringify({
          parts,
          upload_id: this.source.uploadId,
          key: this.source.key,
        }),
      });
      return payload;
    },

    async cancel() {
      if (!this.source) return;

      const { payload } = await this.requestPayload(`${this.videoSourcesUrl}/${this.source.id}`, {
        method: "DELETE",
        body: JSON.stringify({
          upload_id: this.source.uploadId,
          key: this.source.key,
        }),
      });
      this.source = null;
      this.completedParts = [];
      this.resumed = false;
      return payload;
    },

    request(url, options) {
      const token = csrfToken();
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      if (token) headers["X-CSRF-Token"] = token;

      return fetcher(url, { ...options, headers });
    },

    async requestPayload(url, options) {
      let response;
      try {
        response = await this.request(url, options);
      } catch (_error) {
        throw new Error("upload_failed");
      }

      let payload;
      try {
        payload = await response.json();
      } catch (_error) {
        throw new Error("upload_failed");
      }
      if (!response.ok) throw new Error(payload.error || "upload_failed");

      return { response, payload };
    },
  };
}
